<div class="container-fluid complyon-page contracts-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell contracts-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title contracts-title mb-2 text-l">Contract lifecycle</h1>
      <p class="complyon-subtitle contracts-subtitle mb-0 text-s">
        Active contracts - obligations, renewals, change controls and clause risk. Initial signing happens in OnboardOS.
      </p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'register'">
      <section class="mb-3">
        <div class="rounded-4 border px-3 py-3" style="background:#e8f8f5;border-color:#bce9df;">
          <div class="d-flex align-items-start gap-2 text-s">
            <i class="bi bi-link-45deg mt-1 text-success"></i>
            <div>
              <strong>Contract lifecycle.</strong>
              Every contract connects to its supplier, procurement record, payments, risks and incidents - one thread from award to close-out.
            </div>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div
          v-for="card in register_cards"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article class="complyon-card contracts-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label contracts-section-title text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value contracts-section-title mt-1 text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-success'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note contracts-section-subtitle mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel contracts-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title contracts-section-title text-s">Contract register</div>
            <div class="complyon-section-subtitle contracts-section-subtitle text-xs">Owner, supplier, term, value and risk - open one for SLAs, deliverables, approvals and close-out.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action contracts-action-btn" @click="open_contract_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New contract</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table contracts-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Ref</th>
                <th>Contract</th>
                <th>Supplier</th>
                <th>Owner</th>
                <th>Value</th>
                <th>Renewal</th>
                <th>Risk</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in contracts"
                :key="row.id"
                class="contracts-table-row"
                role="button"
                tabindex="0"
                @click="open_contract_drawer(row)"
                @keydown.enter.prevent="open_contract_drawer(row)"
                @keydown.space.prevent="open_contract_drawer(row)"
              >
                <td class="ps-3">
                  <div class="contracts-id">{{ row.id }}</div>
                </td>
                <td>
                  <div class="contracts-name">{{ row.contract }}</div>
                </td>
                <td>{{ row.supplier }}</td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.value }}</td>
                <td>
                  <span class="badge complyon-badge contracts-badge text-xs" :class="row.renewal.class">{{ row.renewal.label }}</span>
                </td>
                <td>
                  <span class="badge complyon-badge contracts-badge text-xs" :class="row.risk.class">{{ row.risk.label }}</span>
                </td>
                <td class="pe-3 text-end">
                  <i class="bi bi-chevron-right contracts-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'renewals'">
      <section class="complyon-panel contracts-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title contracts-section-title text-s">Renewal alerts</div>
            <div class="complyon-section-subtitle contracts-section-subtitle text-xs">Contracts by end date - Complyon alerts the owner inside the notice window.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table contracts-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3"></th>
                <th>Contract</th>
                <th>Owner</th>
                <th>End date</th>
                <th>Days left</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in renewal_rows"
                :key="`${row.id}-${row.end_date}`"
                class="contracts-table-row"
                role="button"
                tabindex="0"
                @click="open_contract_drawer(row.id)"
                @keydown.enter.prevent="open_contract_drawer(row.id)"
                @keydown.space.prevent="open_contract_drawer(row.id)"
              >
                <td class="ps-3">
                  <i class="bi" :class="row.icon"></i>
                </td>
                <td>
                  <div class="contracts-name">{{ row.contract }}</div>
                  <div class="contracts-request-meta">{{ row.supplier }}</div>
                </td>
                <td>{{ row.owner }}</td>
                <td class="fw-semibold">{{ row.end_date }}</td>
                <td :class="row.days_class">{{ row.days_left }}</td>
                <td>
                  <span class="badge complyon-badge contracts-badge text-xs" :class="row.status.class">{{ row.status.label }}</span>
                </td>
                <td class="pe-3 text-end">
                  <i class="bi bi-chevron-right contracts-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'sla'">
      <section class="row g-3 mb-3">
        <div
          v-for="card in sla_cards"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article class="complyon-card contracts-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label contracts-section-title text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value contracts-section-title mt-1 text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-success'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note contracts-section-subtitle mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel contracts-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title contracts-section-title text-s">SLA obligations</div>
            <div class="complyon-section-subtitle contracts-section-subtitle text-xs">Service-level commitments across all contracts, with performance status.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table contracts-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Contract</th>
                <th>Obligation</th>
                <th>Target</th>
                <th>Note</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in sla_rows"
                :key="`${row.contract}-${row.obligation}`"
                class="contracts-table-row"
                role="button"
                tabindex="0"
                @click="open_contract_drawer(contract_id_for_name(row.contract))"
                @keydown.enter.prevent="open_contract_drawer(contract_id_for_name(row.contract))"
                @keydown.space.prevent="open_contract_drawer(contract_id_for_name(row.contract))"
              >
                <td class="ps-3">
                  <div class="contracts-name">{{ row.contract }}</div>
                </td>
                <td>{{ row.obligation }}</td>
                <td>{{ row.target }}</td>
                <td>{{ row.note }}</td>
                <td>
                  <span class="badge complyon-badge contracts-badge text-xs" :class="row.status.class">{{ row.status.label }}</span>
                </td>
                <td class="pe-3 text-end">
                  <i class="bi bi-chevron-right contracts-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="complyon-panel contracts-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title contracts-section-title text-s">Deliverables tracking</div>
            <div class="complyon-section-subtitle contracts-section-subtitle text-xs">Contracted deliverables and their delivery status.</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table contracts-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Contract</th>
                <th>Deliverable</th>
                <th>Due</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in deliverable_rows"
                :key="`${row.contract}-${row.deliverable}`"
                class="contracts-table-row"
                role="button"
                tabindex="0"
                @click="open_contract_drawer(contract_id_for_name(row.contract))"
                @keydown.enter.prevent="open_contract_drawer(contract_id_for_name(row.contract))"
                @keydown.space.prevent="open_contract_drawer(contract_id_for_name(row.contract))"
              >
                <td class="ps-3">
                  <div class="contracts-name">{{ row.contract }}</div>
                </td>
                <td>{{ row.deliverable }}</td>
                <td class="fw-semibold">{{ row.due }}</td>
                <td>
                  <span class="badge complyon-badge contracts-badge text-xs" :class="row.status.class">{{ row.status.label }}</span>
                </td>
                <td class="pe-3 text-end">
                  <i class="bi bi-chevron-right contracts-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>

  <div v-if="active_contract_drawer" class="command-centre-drawer-backdrop" @click="close_contract_drawer">
    <aside class="command-centre-drawer contracts-drawer" role="dialog" aria-modal="true" :aria-label="active_contract_drawer.title" @click.stop>
      <header class="command-centre-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="command-centre-drawer-kicker">{{ active_contract_drawer.section }}</div>
            <div class="command-centre-drawer-title mt-1">{{ active_contract_drawer.title }}</div>
            <div class="command-centre-drawer-subtitle mt-1">{{ active_contract_drawer.subtitle }}</div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="badge contracts-badge" :class="active_contract_drawer.status_class">{{ active_contract_drawer.status }}</span>
            <button type="button" class="btn btn-link p-0 contracts-modal-close" @click="close_contract_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </header>

      <div ref="contractDrawerBody" class="command-centre-drawer-body contracts-drawer-body">
        <section class="mb-3">
          <div class="row g-2">
            <div class="col-6">
              <div class="command-centre-drawer-meta border rounded-3 p-3 h-100">
                <div class="command-centre-drawer-meta-label text-uppercase">Term</div>
                <div class="command-centre-drawer-meta-value mt-1">{{ active_contract_drawer.term }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="command-centre-drawer-meta border rounded-3 p-3 h-100">
                <div class="command-centre-drawer-meta-label text-uppercase">Renewal</div>
                <div class="command-centre-drawer-meta-value mt-1">{{ active_contract_drawer.renewal }}</div>
                <div class="contracts-drawer-meta-hint" :class="`is-${active_contract_drawer.renewal_tone}`">{{ active_contract_drawer.renewal_label }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="command-centre-drawer-meta border rounded-3 p-3 h-100">
                <div class="command-centre-drawer-meta-label text-uppercase">Value</div>
                <div class="command-centre-drawer-meta-value mt-1">{{ active_contract_drawer.value }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="command-centre-drawer-meta border rounded-3 p-3 h-100">
                <div class="command-centre-drawer-meta-label text-uppercase">Risk rating</div>
                <div class="contracts-drawer-risk-value mt-1">{{ active_contract_drawer.risk_value }}</div>
                <div class="contracts-drawer-meta-hint" :class="`is-${active_contract_drawer.risk_tone}`">{{ active_contract_drawer.risk_label }}</div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="active_contract_drawer.connected_records.length" class="mb-3">
          <div class="command-centre-drawer-section-title mb-2">Connected records</div>
          <div class="row g-2">
            <div v-for="record in active_contract_drawer.connected_records" :key="`${record.type}-${record.title}`" class="col-6">
              <div class="contracts-drawer-linked-card">
                <div class="contracts-drawer-linked-label">{{ record.type }}</div>
                <div class="contracts-drawer-linked-value">{{ record.title }}</div>
                <button type="button" class="btn btn-link p-0 contracts-drawer-linked-action" aria-label="Open linked record">
                  <i class="bi bi-box-arrow-up-right"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div class="command-centre-drawer-section-title">SLA obligations</div>
            <button type="button" class="btn btn-link p-0 contracts-drawer-add-btn text-decoration-none">
              <i class="bi bi-plus-lg"></i>
              <span>Add</span>
            </button>
          </div>
          <div class="d-flex flex-column gap-2">
            <div v-for="item in active_contract_drawer.obligations" :key="item.title" class="contracts-drawer-item">
              <div class="d-flex align-items-start gap-2">
                <span class="command-centre-drawer-item-icon" :class="drawer_tone_class(item.tone)">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0 flex-grow-1">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                </div>
                <span class="badge contracts-badge" :class="item.badge_class">{{ item.badge }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div class="command-centre-drawer-section-title">Deliverables</div>
            <button type="button" class="btn btn-link p-0 contracts-drawer-add-btn text-decoration-none">
              <i class="bi bi-plus-lg"></i>
              <span>Add</span>
            </button>
          </div>
          <div class="d-flex flex-column gap-2">
            <div v-for="item in active_contract_drawer.deliverables" :key="item.title" class="contracts-drawer-item">
              <div class="d-flex align-items-start gap-2">
                <span class="command-centre-drawer-item-icon" :class="drawer_tone_class(item.tone)">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0 flex-grow-1">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                </div>
                <span class="badge contracts-badge" :class="item.badge_class">{{ item.badge }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div class="command-centre-drawer-section-title">Variations & addendums</div>
            <div class="d-flex align-items-center gap-3">
              <button type="button" class="btn btn-link p-0 contracts-drawer-add-btn text-decoration-none">
                <i class="bi bi-plus-lg"></i>
                <span>Variation</span>
              </button>
              <button type="button" class="btn btn-link p-0 contracts-drawer-add-btn text-decoration-none">
                <i class="bi bi-plus-lg"></i>
                <span>Addendum</span>
              </button>
            </div>
          </div>
          <div v-if="active_contract_drawer.variations.length" class="d-flex flex-column gap-2">
            <div v-for="item in active_contract_drawer.variations" :key="item.title" class="contracts-drawer-item">
              <div class="d-flex align-items-start gap-2">
                <span class="command-centre-drawer-item-icon" :class="drawer_tone_class(item.tone)">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0 flex-grow-1">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                </div>
                <span class="badge contracts-badge" :class="item.badge_class">{{ item.badge }}</span>
              </div>
            </div>
          </div>
          <div v-else class="contracts-drawer-empty-copy">No variations or addendums.</div>
        </section>

        <section v-if="active_contract_drawer.approvals.length" class="mb-3">
          <div class="command-centre-drawer-section-title mb-2">Legal approval history</div>
          <div class="command-centre-drawer-timeline">
            <div v-for="(item, index) in active_contract_drawer.approvals" :key="item.title" class="command-centre-drawer-timeline-item">
              <span class="command-centre-drawer-timeline-badge" :class="drawer_tone_class(item.tone)">
                <i class="bi bi-check-lg"></i>
              </span>
              <div class="command-centre-drawer-timeline-content">
                <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                <div class="command-centre-drawer-timeline-meta">{{ item.subtitle }}</div>
              </div>
              <span v-if="index < active_contract_drawer.approvals.length - 1" class="command-centre-drawer-timeline-line"></span>
            </div>
          </div>
        </section>

        <section v-if="active_contract_drawer.checklist.length" class="mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
            <div class="command-centre-drawer-section-title">Close-out checklist</div>
            <div class="contracts-drawer-progress-count">{{ active_contract_drawer.checklist_progress }}</div>
          </div>
          <div class="d-flex flex-column gap-2">
            <div v-for="item in active_contract_drawer.checklist" :key="item.title" class="contracts-drawer-check-row">
              <div class="d-flex align-items-center gap-2 min-w-0">
                <span class="contracts-drawer-check-dot" :class="drawer_tone_class(item.tone)"></span>
                <span class="contracts-drawer-check-label">{{ item.title }}</span>
              </div>
              <span class="badge contracts-badge" :class="item.status_class">{{ item.status }}</span>
            </div>
          </div>
        </section>
      </div>

      <footer class="command-centre-drawer-footer border-top">
        <div class="d-flex align-items-center justify-content-between gap-2 w-100">
          <button type="button" class="btn contracts-drawer-evidence-btn">
            <i class="bi bi-archive"></i>
            <span>Export evidence pack</span>
          </button>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2">
            <i class="bi bi-pencil-square"></i>
            <span>Edit contract</span>
          </button>
        </div>
      </footer>
    </aside>
  </div>

  <div v-if="contract_modal_open" class="contracts-modal-backdrop" @click.self="close_contract_modal">
    <section class="contracts-modal-shell" role="dialog" aria-modal="true" aria-label="New contract">
      <header class="contracts-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="contracts-modal-title">New contract</div>
          <div class="contracts-modal-subtitle">Register a contract. SLAs, deliverables, variations and approvals are managed in the contract detail.</div>
        </div>
        <button type="button" class="btn btn-link p-0 contracts-modal-close" @click="close_contract_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="contracts-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="contracts-modal-label">Contract name</label>
            <input v-model="contract_form.contract" type="text" class="form-control contracts-modal-input" placeholder="e.g. Facilities maintenance agreement">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">Supplier / counterparty</label>
            <input v-model="contract_form.supplier" type="text" class="form-control contracts-modal-input" placeholder="Supplier name">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">Contract owner</label>
            <input v-model="contract_form.owner" type="text" class="form-control contracts-modal-input" placeholder="Dept - name">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">Start date</label>
            <input v-model="contract_form.start_date" type="text" class="form-control contracts-modal-input" placeholder="e.g. 01 Mar 2026">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">End date</label>
            <input v-model="contract_form.end_date" type="text" class="form-control contracts-modal-input" placeholder="e.g. 28 Feb 2028">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">Value (R)</label>
            <input v-model="contract_form.value" type="text" class="form-control contracts-modal-input" placeholder="Amount">
          </div>

          <div class="col-12 col-md-6">
            <label class="contracts-modal-label">Risk rating (0-100)</label>
            <input v-model="contract_form.risk_rating" type="text" class="form-control contracts-modal-input" placeholder="40">
          </div>

          <div class="col-12">
            <label class="contracts-modal-label">Status</label>
            <select v-model="contract_form.status" class="form-select contracts-modal-input">
              <option v-for="option in contract_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>

      <footer class="contracts-modal-footer d-flex align-items-center justify-content-end gap-2 border-top">
        <button type="button" class="btn btn-link contracts-modal-cancel text-decoration-none" @click="close_contract_modal">Cancel</button>
        <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_contract_modal">
          <i class="bi bi-plus-lg"></i>
          <span>Create</span>
        </button>
      </footer>
    </section>
  </div>
</div>
